// app/src/features/timeline/renderers/renderPromotedEvt.jsx
// Timeline render helpers moved verbatim out of AppInner (app/src/App.jsx) by PKG-04 module #1. Each
// takes the column's render context first; the original parameters follow.
import React from "react";
import { C } from "../../../lib/colors.js";
import { eventKey } from "../../../lib/forecast/eventIdentity.js";
import { ui } from "../../../lib/telemetry/instrument.js";
import { BASE_R, LABEL_H, LABEL_MIN_PPD, evtScale } from "../../../lib/timeline/geometry.js";
import { haloBox } from "../../../lib/timeline/hitHalo.js";
import { LABEL_FADE_MS } from "../../../lib/timeline/labelLayout.js";
import { clampLabelShift } from "../../../lib/timeline/laneBand.js";
import {
  computeEventGeom,
  renderEventPie,
  renderLabelTether,
  resolveEventStatus,
  rowKey,
} from "./eventHelpers.jsx";
import { renderEventBell } from "./renderEventBell.jsx";
import {
  LABEL_GAP_SCALE,
  NEUTRAL_LABEL_GAP_SCALE,
  STREAM_HALF,
  TETHER_LINE_H,
} from "./constants.js";

// Opens a trigger's label from its dot as the NEWEST open label, so it wins its spot and the
// overlap pass (computeVisibility) closes any older open label it now overlaps back to a dot.
// Re-clicking an open-but-overlapped (dotted) label re-opens it the same way. Which labels
// collide is left entirely to that pass: it knows the side each label is actually drawn on and
// the x its circle actually occupies.
export function togglePromoted(ctx, rowId, evId) {
  const { setCollapsedIds, setPromotedIds } = ctx;
  const key = rowKey(rowId, evId);
  setPromotedIds((prev) => {
    const next = new Set(prev);
    next.delete(key);
    next.add(key); // re-insert last: Set order is click order
    return next;
  });
  setCollapsedIds((prev) => {
    if (!prev.has(key)) return prev;
    const next = new Set(prev);
    next.delete(key);
    return next;
  });
}

export function renderMiniDot(
  ctx,
  ev,
  sx,
  spineY,
  opacity,
  keyPrefix,
  aboveOverride,
  rowId = "main",
) {
  const { cSize, hovEvtRef, laneLeftEdge, redBlueTeam, setHov, setHovPos } = ctx;
  if (sx < Math.max(-20, laneLeftEdge - 12) || sx > cSize.w + 20) return null;
  // Multi-value ("multiway") triggers are neutral even in mini-dot form: never team red/blue,
  // since a distribution has no up/down direction.
  const dotCol = ev._neutral
    ? ev.isPast
      ? C.gray
      : C.grayL
    : redBlueTeam && !ev.isPast
      ? ev.direction === "yes"
        ? C.blueTeam
        : C.redTeam
      : C.gray;
  const sc = evtScale(ev),
    r = Math.round(BASE_R * sc);
  const channelOffset = STREAM_HALF * 0.55;
  const above = aboveOverride != null ? aboveOverride : ev.direction === "yes";
  const circleY = ev._neutral
    ? spineY - r
    : above
      ? spineY - channelOffset - r
      : spineY + channelOffset - r;
  // No nudge: the dashed spine is centered on spineY (see renderEvt), so a dot centered
  // there lands on the line's visual centre.
  const centerY = circleY + r;
  const dotSz = 5;
  const hitSz = Math.ceil(dotSz * 1.25 * 2);
  return (
    <div
      key={keyPrefix + rowId + "::" + eventKey(ev)}
      data-testid={`evt-dot-${ev.id}`}
      className="tl-fade"
      data-occurrence-key={eventKey(ev)}
      {...ui("timeline.event-dot", ev.id)}
      onClick={() => togglePromoted(ctx, rowId, eventKey(ev))}
      onMouseEnter={(e) => {
        hovEvtRef.current = ev;
        setHov("dot::" + rowId + "::" + eventKey(ev));
        setHovPos({ x: e.clientX, y: e.clientY });
      }}
      onMouseMove={(e) => setHovPos({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => {
        hovEvtRef.current = null;
        setHov(null);
      }}
      style={{
        position: "absolute",
        left: sx - hitSz / 2,
        top: centerY - hitSz / 2,
        width: hitSz,
        height: hitSz,
        borderRadius: "50%",
        cursor: "pointer",
        zIndex: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Fades in as a label closes into it — the other half of the label's fade-out.
        animation: `simpleFadeIn ${LABEL_FADE_MS}ms ease-out both`,
      }}
    >
      <div
        style={{
          width: dotSz,
          height: dotSz,
          borderRadius: "50%",
          background: dotCol,
          opacity,
          transition: "opacity 0.15s",
        }}
      />
    </div>
  );
}

export function renderPromotedEvt(
  ctx,
  ev,
  sx,
  spineY,
  color,
  above,
  rowId,
  branchForkMs = null,
  haloR = null,
) {
  const {
    active,
    cSize,
    forecastTracking,
    hovEvtRef,
    laneLeftEdge,
    ppd,
    setHov,
    setHovPos,
    setSelEvt,
  } = ctx;
  const { hovKey, r, d, ih, col, labelCol, circleY } = computeEventGeom(
    ctx,
    ev,
    rowId,
    color,
    above,
    spineY,
  );
  const { isResolvedOccurred, isResolvedNotOccurred } = resolveEventStatus(
    ev,
    active,
    forecastTracking,
  );
  const halo = haloBox(r, circleY, haloR);
  const neutralLabelGap = (offset) => r + (offset - r) * NEUTRAL_LABEL_GAP_SCALE * LABEL_GAP_SCALE;
  const labelY = ev._neutral
    ? above
      ? spineY - neutralLabelGap(STREAM_HALF) - LABEL_H
      : spineY + neutralLabelGap(STREAM_HALF + 4)
    : above
      ? circleY - LABEL_H - 2
      : spineY + (STREAM_HALF + 4) * LABEL_GAP_SCALE;
  const promoFit = clampLabelShift(sx, 104, { minX: laneLeftEdge, maxX: cSize.w || 800 });

  return (
    <React.Fragment key={"promo-" + eventKey(ev)}>
      {renderEventBell(ctx, ev, sx, rowId, above, spineY, circleY, r, ih, branchForkMs)}
      <div style={{ position: "absolute", left: sx, top: 0, zIndex: ih ? 20 : 10 }}>
        <div
          data-tour-spotlight={!ev.isPast ? "trigger" : undefined}
          data-tour-trigger-dir={!ev.isPast ? (ev.direction === "no" ? "no" : "yes") : undefined}
          onMouseEnter={(e) => {
            hovEvtRef.current = ev;
            setHov(hovKey);
            setHovPos({ x: e.clientX, y: e.clientY });
          }}
          onMouseMove={(e) => setHovPos({ x: e.clientX, y: e.clientY })}
          onMouseLeave={() => {
            hovEvtRef.current = null;
            setHov(null);
          }}
          style={{
            position: "absolute",
            left: halo.left,
            top: halo.top,
            width: halo.size,
            height: halo.size,
            borderRadius: "50%",
            pointerEvents: "auto",
            zIndex: 9,
          }}
        >
          <div
            data-testid={`evt-pie-${ev.id}`}
            data-clears-question=""
            data-occurrence-key={eventKey(ev)}
            {...ui("timeline.event", ev.id)}
            onClick={() => setSelEvt({ ...ev, _rowId: rowId })}
            style={{
              position: "absolute",
              left: halo.inset,
              top: halo.inset,
              cursor: "pointer",
              transition: "transform .12s",
              transform: ih ? "scale(1.15)" : "scale(1)",
              transformOrigin: "center",
              animation: "popInSlow 0.7s ease-out forwards",
            }}
          >
            {renderEventPie(ctx, ev, r, d, ih, col, isResolvedOccurred, isResolvedNotOccurred)}
          </div>
        </div>
        {ppd >= LABEL_MIN_PPD && !promoFit.hidden && (
          <>
            {renderLabelTether(ev, above, labelY, circleY, r, LABEL_H / TETHER_LINE_H)}
            <div
              data-tour-label=""
              data-clears-question=""
              style={{
                position: "absolute",
                left: -52 + promoFit.shift,
                top: labelY,
                width: 104,
                height: LABEL_H,
                display: "flex",
                flexDirection: "column",
                justifyContent: above ? "flex-end" : "flex-start",
                alignItems: "center",
                textAlign: "center",
                pointerEvents: "none",
              }}
            >
              {/* Handlers are inline here because this function has its own halo handlers
                   rather than renderEvt's extracted trio. */}
              <span
                {...ui("timeline.event-label", ev.id)}
                onMouseEnter={(e) => {
                  hovEvtRef.current = ev;
                  setHov(hovKey);
                  setHovPos({ x: e.clientX, y: e.clientY });
                }}
                onMouseMove={(e) => setHovPos({ x: e.clientX, y: e.clientY })}
                onMouseLeave={() => {
                  hovEvtRef.current = null;
                  setHov(null);
                }}
                onClick={() => setSelEvt({ ...ev, _rowId: rowId })}
                title={ev.title}
                style={{
                  pointerEvents: "auto",
                  cursor: "pointer",
                  fontSize: 12,
                  lineHeight: "15px",
                  color: ih ? C.white : labelCol,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  maxWidth: 104,
                }}
              >
                {ev.title}
              </span>
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  );
}
